import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useInView } from 'motion/react'

import { useSectionViewTrack } from '@/hooks/useSectionViewTrack'
import { TIPI_BUSINESS_COUNT } from '@/lib/landingStats/constants'
import { GuiaRepsolHacemosCocinaLogo } from '@/shared/ui/GuiaRepsolHacemosCocinaLogo'
import NumberFlow, { continuous } from '@number-flow/react'
import type { LandingPage } from '@tipi/shared'

import BasqueLogo from '../../assets/images/basque-logo.svg?react'
import GOELogo from '../../assets/images/tipi-goe.svg?react'
import { RestaurantNamesMarquee } from './RestaurantNamesMarquee'
import { ASSETS, FOOD_COST_LANDING_PAGE } from './constants'

const ORGANIZATIONS = ['hacemosCocina', 'jovenesTalentos', 'goe', 'lanzadera'] as const
type Organization = (typeof ORGANIZATIONS)[number]

const ORGANIZATION_NAME_KEYS: Record<Organization, string> = {
    hacemosCocina: 'foodCost.authority.credentials.hacemosCocina.logoAlt',
    jovenesTalentos: 'foodCost.authority.credentials.jovenesTalentos.name',
    goe: 'foodCost.authority.credentials.goe.name',
    lanzadera: 'foodCost.authority.credentials.lanzadera.name',
}

const BUSINESS_COUNT_CLASS_NAME =
    'text-[56px] font-medium tabular-nums leading-none tracking-[-2px] text-[#F4502A] [--number-flow-mask-width:0em] md:text-[96px] md:tracking-[-3px]'

const ORGANIZATION_SVG_CLASS = 'max-h-full max-w-full'

const GROUP_TITLE_CLASS =
    'm-0 text-[13px] font-bold uppercase tracking-[0.14em] text-[#17130E] text-center md:text-left'

interface FoodCostAuthoritySectionProps {
    landingPage?: LandingPage
    mixpanelContext?: Record<string, unknown>
}

export function FoodCostAuthoritySection({
    landingPage = FOOD_COST_LANDING_PAGE,
    mixpanelContext,
}: FoodCostAuthoritySectionProps) {
    const { t } = useTranslation()
    const sectionRef = useRef(null)
    const numberRef = useRef(null)
    const isNumberInView = useInView(numberRef, { once: true, amount: 'all', margin: '0px 0px -10% 0px' })
    const [hasMounted, setHasMounted] = useState(false)
    const [businessCount, setBusinessCount] = useState(0)

    useEffect(() => {
        setHasMounted(true)
    }, [])

    useSectionViewTrack(sectionRef, 'Authority', { landing_page: landingPage, ...mixpanelContext })

    useEffect(() => {
        if (isNumberInView) setBusinessCount(TIPI_BUSINESS_COUNT)
    }, [isNumberInView])

    return (
        <section ref={sectionRef} className='bg-[#F5EEE4] pb-12 pt-12 md:pb-16 md:pt-20'>
            <div className='mx-auto flex max-w-[1180px] flex-col gap-2.5 px-[22px] text-center md:px-10 md:text-left'>
                <h2 className='m-0 text-[32px] font-medium leading-[1.15] tracking-[-1px] text-[#F4502A] md:text-[36px]'>
                    {t('foodCost.authority.title')}
                </h2>
                <p className='m-0 mx-auto max-w-[60ch] text-[15px] leading-[1.65] text-[#5C5548] md:mx-0 md:text-[17px] md:leading-[1.7]'>
                    {t('foodCost.authority.answerCapsule', { count: TIPI_BUSINESS_COUNT })}
                </p>
            </div>

            <div className='mx-auto mt-8 max-w-[1180px] px-[22px] md:mt-12 md:px-10'>
                <h3 className={GROUP_TITLE_CLASS}>
                    {t('foodCost.authority.restaurantsTitle', 'Restaurantes que confían en Tipi')}
                </h3>
                <div className='mt-4 grid grid-cols-[auto_minmax(0,1fr)] items-center gap-4 md:mt-6 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:gap-10'>
                    <div ref={numberRef} className='flex flex-col gap-1'>
                        {hasMounted ? (
                            <NumberFlow
                                value={businessCount}
                                format={{ minimumIntegerDigits: String(TIPI_BUSINESS_COUNT).length }}
                                plugins={[continuous]}
                                transformTiming={{ duration: 1400, easing: 'ease-out' }}
                                spinTiming={{ duration: 1400, easing: 'ease-out' }}
                                className={BUSINESS_COUNT_CLASS_NAME}
                            />
                        ) : (
                            <span className={BUSINESS_COUNT_CLASS_NAME}>{TIPI_BUSINESS_COUNT}</span>
                        )}
                        <p className='m-0 text-[14px] leading-snug text-[#5C5548] md:text-base'>
                            {t('foodCost.authority.clientCountDescriptionLine1')}
                            <br />
                            {t('foodCost.authority.clientCountDescriptionLine2')}
                        </p>
                    </div>
                    <div className='overflow-hidden rounded-[18px] bg-[#E8E1D5]'>
                        <img
                            src={ASSETS.map}
                            alt={t('foodCost.authority.mapAlt')}
                            loading='lazy'
                            decoding='async'
                            className='h-[150px] w-full object-cover md:h-[320px] md:object-contain'
                        />
                    </div>
                </div>
            </div>

            <RestaurantNamesMarquee className='mt-8 md:mt-10' />

            <div className='mx-auto mt-10 max-w-[1180px] px-[22px] md:mt-14 md:px-10'>
                <h3 className={GROUP_TITLE_CLASS}>
                    {t('foodCost.authority.organizationsTitle', 'Organizaciones que avalan Tipi')}
                </h3>
                <ul className='m-0 mt-4 grid list-none grid-cols-2 gap-2.5 p-0 md:mt-6 md:grid-cols-4 md:gap-4'>
                    {ORGANIZATIONS.map(organization => (
                        <li
                            key={organization}
                            className='flex flex-col items-center justify-center gap-2 rounded-[16px] border border-[#D8CFC0] px-3 py-4 text-center'
                        >
                            <div className='flex h-9 w-full items-center justify-center md:h-11'>
                                <OrganizationLogo organization={organization} />
                            </div>
                            <span className='sr-only'>{t(ORGANIZATION_NAME_KEYS[organization])}</span>
                            <span className='text-[12px] leading-snug text-[#8A7F6F] md:text-[13px]'>
                                {t(`foodCost.authority.credentials.${organization}.label`)}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}

function OrganizationLogo({ organization }: { organization: Organization }) {
    if (organization === 'hacemosCocina') {
        // Stacked in the tile: hide the divider that only works on one line.
        return (
            <GuiaRepsolHacemosCocinaLogo className='flex-col justify-center gap-y-0.5 text-[14px] text-[#17130E] md:text-[15px] [&>span:nth-child(2)]:hidden' />
        )
    }
    if (organization === 'goe') return <GOELogo className={ORGANIZATION_SVG_CLASS} aria-hidden />
    if (organization === 'jovenesTalentos') return <BasqueLogo className={ORGANIZATION_SVG_CLASS} aria-hidden />
    // The Lanzadera mark is a padded glyph; pair it with the wordmark so it reads at tile size.
    return (
        <span className='flex h-full items-center gap-1' aria-hidden>
            <img
                src={ASSETS.lanzaderaLogo}
                alt=''
                loading='lazy'
                decoding='async'
                className='h-[160%] w-auto object-contain'
            />
            <span className='text-[15px] font-semibold tracking-[-0.01em] text-[#003366] md:text-[17px]'>
                Lanzadera
            </span>
        </span>
    )
}
